import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Fade,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  AlertComponent,
  ModalComponent,
  NavBar,
  ProductForm,
} from "../../components";
import DataContext from "../../context/DataContext";
import { useFetch, usePermissions } from "../../hooks";
import { usePaginator } from "../../hooks/usePaginator";
import { ProductProps } from "../../interfaces/interfaces";
import EditIcon from "@mui/icons-material/Edit";
import { initial_tool_state } from "../../utils/data";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  ListColumn,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

interface FetchResponse {
  success?: boolean;
  data: ProductProps[];
}

const initialProductState: ProductProps = {
  id: 0,
  partNumber: "",
  name: "",
  description: "",
  supplier: "",
  topThreadConnection: "",
  bottomThreadConnection: "",
  status: { id: 0, name: "" },
  maxOD: "",
  bodyOD: "",
  length: 0,
  quantity: 1,
  weight: 0,
  osi: false,
  tailJoint: false,
  espAssembly: false,
  imagePath: null,
  size: 0,
  isCompound: false,
};

export default function ProductPage() {
  const { isSuccess, isError, onCreateTool, onEditTool } = useContext(DataContext);
  const { paginationModel, setPaginationModel } = usePaginator("products");
  const [search, setSearch] = useState("");
  const fetchFilters = useMemo(() => ({ search }), [search]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [productInfo, setProductInfo] =
    useState<ProductProps>(initialProductState);
  const { canManageProducts } = usePermissions();

  const { data, pagination, isLoading, error, fetchData } =
    useFetch<FetchResponse>(
      "product-paginate",
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );

  const [tableData, setTableData] = useState<ProductProps[]>([]);

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const handleEditProduct = (product: ProductProps) => {
    setProductInfo(product);
    setIsOpenModal(true);
  };

  const columns: ListColumn<ProductProps>[] = [
    {
      key: "partNumber",
      header: "Part Number",
      render: (r) => r.partNumber,
      sortAccessor: (r) => r.partNumber,
      width: "1.1fr",
    },
    {
      key: "name",
      header: "Name",
      render: (r) => r.name,
      sortAccessor: (r) => r.name,
      width: "1.6fr",
    },
    {
      key: "topThreadConnection",
      header: "Top Thread",
      render: (r) => r.topThreadConnection ?? "",
      sortAccessor: (r) => r.topThreadConnection ?? "",
      width: "1fr",
    },
    {
      key: "bottomThreadConnection",
      header: "Bottom Thread",
      render: (r) => r.bottomThreadConnection ?? "",
      sortAccessor: (r) => r.bottomThreadConnection ?? "",
      width: "1fr",
    },
    {
      key: "maxOD",
      header: "Max OD",
      render: (r) => r.maxOD ?? "",
      sortAccessor: (r) => r.maxOD ?? "",
      width: "0.7fr",
    },
    {
      key: "bodyOD",
      header: "Body OD",
      render: (r) => r.bodyOD ?? "",
      sortAccessor: (r) => r.bodyOD ?? "",
      width: "0.7fr",
    },
    {
      key: "length",
      header: "Length",
      render: (r) => r.length ?? "",
      sortAccessor: (r) => r.length ?? null,
      width: "0.6fr",
      align: "right",
    },
    {
      key: "weight",
      header: "Weight",
      render: (r) => r.weight ?? "",
      sortAccessor: (r) => r.weight ?? null,
      width: "0.6fr",
      align: "right",
    },
    {
      key: "description",
      header: "Description",
      render: (r) => r.description ?? "",
      sortAccessor: (r) => r.description ?? "",
      width: "1.6fr",
    },
  ];

  return (
    <>
      <NavBar title="Products" buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <ListPageLayout<ProductProps>
                title="Products"
                columns={columns}
                rows={tableData}
                getRowId={(r) => r.id}
                rowsLoading={isLoading}
                isError={Boolean(error)}
                errorMessage="Products could not be loaded."
                searchValue={search}
                onSearchChange={(value) => setSearch(value.trim())}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalCount={Number(pagination?.totalRecords || 0)}
                onPageChange={(page) =>
                  setPaginationModel({ ...paginationModel, page })
                }
                onPageSizeChange={(pageSize) =>
                  setPaginationModel({ page: 0, pageSize })
                }
                toolbarActions={
                  canManageProducts ? (
                    <Button
                      startIcon={<AddBoxIcon />}
                      onClick={() => {
                        setProductInfo(initial_tool_state);
                        setIsOpenModal(true);
                      }}
                    >
                      New
                    </Button>
                  ) : undefined
                }
                rowActions={(r) => (
                  <>
                    {canManageProducts && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleEditProduct(r)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              />
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenModal && (
        <ModalComponent
          modalContent={
            productInfo.id === 0 ? (
              <ProductForm
                productValues={productInfo}
                onCreatetool={(values: ProductProps) => {
                  onCreateTool(values, () => {
                    fetchData();
                    setIsOpenModal(false);
                  });
                }}
                onCancel={() => {
                  setProductInfo(initialProductState);
                  setIsOpenModal(false);
                }}
              />
            ) : (
              <ProductForm
                productValues={productInfo}
                onEdit={(values: ProductProps) => {
                  onEditTool(values, undefined, () => {
                    fetchData();
                    setIsOpenModal(false);
                  });
                }}
                onCancel={() => {
                  setProductInfo(initialProductState);
                  setIsOpenModal(false);
                }}
              />
            )
          }
        />
      )}
    </>
  );
}
